import { useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Check, ArrowRight, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Navbar, Footer, FloatingActions } from "@/components/Chrome";
import { Reveal } from "@/components/motion";
import { SERVICES, IMAGES, BRAND } from "@/lib/data";
import { useEnquiry } from "@/components/Enquiry";
import useLenis from "@/hooks/useLenis";

export default function ServiceDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { open, waLink } = useEnquiry();
  useLenis();
  const service = SERVICES.find((s) => s.slug === slug);

  useEffect(() => { window.scrollTo(0, 0); if (service) document.title = `${service.title} | Smart Living Interiors`; }, [slug, service]);

  if (!service) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4">
        <p className="font-display text-2xl">Service not found</p>
        <Button onClick={() => navigate("/")} className="rounded-full">Back Home</Button>
      </div>
    );
  }

  const gallery = [IMAGES.kitchen4, IMAGES.kitchen1, IMAGES.wardrobe1, IMAGES.kitchen5];
  const others = SERVICES.filter((s) => s.slug !== slug).slice(0, 3);

  return (
    <div className="App overflow-x-hidden">
      <Navbar />
      <section className="relative h-[70vh] min-h-[480px] flex items-end overflow-hidden" data-testid="service-hero">
        <motion.img src={service.image} alt={service.title} initial={{ scale: 1.15 }} animate={{ scale: 1 }} transition={{ duration: 1.4 }} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-black/30" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 w-full pb-14">
          <Link to="/#services" className="inline-flex items-center gap-2 text-white/80 hover:text-white text-sm mb-6" data-testid="service-back"><ArrowLeft className="w-4 h-4" /> All Services</Link>
          <p className="text-gold uppercase tracking-[0.25em] text-xs mb-3">Smart Living Interiors</p>
          <h1 className="font-display text-white font-bold text-4xl md:text-6xl max-w-3xl">{service.title}</h1>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-background">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2">
            <Reveal><p className="text-lg md:text-xl text-muted-foreground leading-relaxed mb-10">{service.desc}</p></Reveal>
            <Reveal delay={0.05}><h2 className="font-display text-2xl mb-5">Key Benefits</h2></Reveal>
            <div className="grid sm:grid-cols-2 gap-4 mb-12">
              {service.benefits.map((b, i) => (
                <Reveal key={b} delay={i * 0.06}>
                  <div className="flex items-start gap-3 bg-secondary/50 rounded-xl p-4">
                    <Check className="w-5 h-5 text-primary mt-0.5 shrink-0" /><span className="text-sm">{b}</span>
                  </div>
                </Reveal>
              ))}
            </div>
            <Reveal><h2 className="font-display text-2xl mb-5">Gallery</h2></Reveal>
            <div className="columns-2 gap-4 [&>*]:mb-4">
              {gallery.map((g, i) => (
                <Reveal key={i} delay={(i % 2) * 0.06}>
                  <div className="img-zoom rounded-xl overflow-hidden"><img src={g} alt="" loading="lazy" className="w-full object-cover" /></div>
                </Reveal>
              ))}
            </div>
          </div>

          <div className="lg:col-span-1">
            <div className="sticky top-28 bg-charcoal text-white rounded-3xl p-8 grain">
              <h3 className="font-display text-2xl mb-3">Interested in {service.title}?</h3>
              <p className="text-white/60 text-sm mb-6">Get a free consultation and a transparent quote tailored to your space.</p>
              <Button onClick={() => open({ project_type: service.title, source: `service-page-${slug}` })} data-testid="service-enquire" className="w-full rounded-full bg-primary text-white hover:bg-primary/90 mb-3">
                Book Free Consultation <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button asChild variant="outline" className="w-full rounded-full bg-white/5 border-white/20 text-white hover:bg-white/10">
                <a href={waLink(`Hi, I'm interested in ${service.title}.`)} target="_blank" rel="noreferrer"><MessageCircle className="w-4 h-4 mr-2" /> WhatsApp</a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-secondary/40">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <h2 className="font-display text-2xl md:text-3xl mb-8">Explore more services</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {others.map((s) => (
              <Link key={s.slug} to={`/services/${s.slug}`} className="group img-zoom relative rounded-2xl overflow-hidden h-56 block">
                <img src={s.image} alt={s.title} loading="lazy" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent flex items-end p-5">
                  <h3 className="font-display text-xl text-white group-hover:text-gold transition-colors">{s.title}</h3>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
      <Footer />
      <FloatingActions />
    </div>
  );
}
